<template>
  <section class="section facilities">
    <template v-if="isLoadingPage">
      <div class="facilities-spinner">
        <SpinnerRadial></SpinnerRadial>
      </div>
    </template>
    <template v-else>
      <div class="facilities-map">
        <div class="facilities-header">
          <FacilitiesFilter
            :filters-list="state.filtersList"
            :is-city-button-clicked="state.isCityButtonClicked"
            @change-level="changeLevel"
            @change-city="handleClickCityButton"
            @open-search-modal="openSearchModal"
            @open-filter-modal="openFilterModal"
          />

          <div class="facilities-modal-container">
            <FacilityObjectListModal
              v-model:is-show-modal="state.isShowSearchModal"
              v-model:search-input="searchString"
              :list="facilitiesListToShow"
              :modalTitle="facilitiesListDefaultTitle"
              :is-loading="isLoadingSearchRequest"
              :is-disabled-teleport="isDisabledTeleport"
              @select-facility-object="openFacilityObjectModal"
            />
            <FacilityObjectModal
              v-if="isOpenFacilityObjectModal"
              :facility-object-id="isOpenFacilityObjectModal"
              :is-disabled-teleport="isDisabledTeleport"
              :selected-level="selectedLevel"
              :subscription-model="subscriptionModel"
              :tab-options="subscriptionModelTabOptions"
              v-model:is-modal-body-scroll-threshold-pass="isFacilityObjectModalBodyScrollThresholdPass"
              @close="closeFacilityObjectModal"
            />
          </div>
        </div>

        <TheMap
          v-if="bounds?.length > 0 && filteredMapMarkers.features"
          v-model:facilityPopupData="facilityPopupData"
          v-model:is-selected-facility-object="state.isSelectedFacilityObject"
          geolocation
          :markers="filteredMapMarkers"
          :fly-to-city="state.flyToCity"
          :bounds="bounds"
          v-model:is-city-button-clicked="state.isCityButtonClicked"
          @get-current-bounds="getCurrentMapBounds"
        />

        <FacilityPopup
          v-if="!!facilityPopupData"
          :facility-popup-object="facilityPopupData"
          @close="closeFacilityPopup"
          @select-facility-object="openFacilityObjectModal"
        />

        <MapFilterModal
          v-model:is-show-modal="state.isShowFilterModal"
          :filters-list="state.filtersList"
          :is-city-button-clicked="state.isCityButtonClicked"
          @change-level="changeLevel"
          @change-city="handleClickCityButton"
        />

        <LocLink to="/facilities-table" class="facilities__objects-table">
          {{ $t("List of facilities (table)") }}
          <BaseIcon icon="arrow" />
        </LocLink>
      </div>
    </template>
  </section>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import type { LngLatLike } from "mapbox-gl";
import { useMapFilterStore } from "~/stores/mapFilter";
import { useCountriesStore } from "~/stores/countries";
import {
  IMapFiltersResponse,
  IMapLngLat,
  IMapMarkersRequest,
  IMapMarkersResponse,
  ISupplier,
  ISupplierPreview,
  CountryBoundsDependingOnOrientationScreen,
} from "~/types/map.interface";
import { ILevelFilter } from "~/server/types/server.interface";
import { BaseSelectOption } from "~/components/input/BaseSelect.vue";
import {
  getMapFilters,
  getMapMarkers,
  getMapSearchList,
} from "~/api/mapRequest";
import BaseIcon from "~/components/icons/BaseIcon.vue";
import FacilitiesFilter from "~/components/facilities/FacilitiesFilter.vue";
import TheMap from "~/components/map/TheMap.vue";
import SpinnerRadial from "~/components/loaders/SpinnerRadial.vue";
import FacilityPopup from "~/components/modals/FacilityPopup.vue";
import FacilityObjectModal from "~/components/modals/FacilityObjectModal.vue";
import FacilityObjectListModal from "~/components/modals/FacilityObjectListModal.vue";
import MapFilterModal from "~/components/modals/MapFilterModal.vue";
import LocLink from "~/components/lang/LocLink.vue";
import { TEST_ACCOUNT_ID } from "~/server/helpers/getSuppliersFromGeneratedSearchByFilters";
import { mapSubscriptionModelToLevels, DESKTOP_SCREEN_WIDTH } from "~/constants";

const { gtag } = useGtag();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const countriesStore = useCountriesStore();
const mapFilterStore = useMapFilterStore();

const {
  selectedLevel,
  activities,
  tags,
  city,
  bounds,
  search: searchString,
  subscriptionModel,
  isLevelSelected,
  subscriptionModelTabOptions,
} = storeToRefs(mapFilterStore);

const {
  setSubscriptionModel,
  setMapFilterLevel,
  setMapFilterLevels,
  setMapFilterCity,
  resetMapFilterCity,
  injectLocalStorageValues,
  resetSearchString,
} = mapFilterStore;

const isLoadingPage = ref<boolean>(true);
const isLoadingMapMarkersRequest = ref<boolean>(false);
const isLoadingSearchRequest = ref<boolean>(false);
const isFacilityObjectModalBodyScrollThresholdPass = ref(false);
const facilityPopupData = ref<ISupplierPreview | null>(null);
const screenWidth = ref<number>(0);

const state = reactive({
  filtersList: {} as IMapFiltersResponse,
  mapMarkers: {} as IMapMarkersResponse,
  searchResult: {
    list: [] as (ISupplier | ISupplierPreview)[],
    listFromOtherCities: [] as (ISupplier | ISupplierPreview)[],
  },
  facilityId: null as number | null | string,
  isShowSearchModal: false as boolean,
  isShowFilterModal: false as boolean,
  isSelectedFacilityObject: false,
  isCityButtonClicked: false,
  flyToCity: undefined as [IMapLngLat, IMapLngLat] | undefined,
  subscriptionModel: 1,
});


// Store only queries (without the search string)
// in the following order: city, level, tags, and activities, subscription model
const searchQueryHistory = new Map<string, (ISupplier | ISupplierPreview)[]>();
const searchController: { controller: AbortController | null, activeRequestCount: number } = {
  controller: null,
  activeRequestCount: 0
};

let debounceTimeout: number | null = null;

const levelTypeBySubscriptionModel = computed(() => mapSubscriptionModelToLevels[subscriptionModel.value]);
const level = computed(() => selectedLevel.value[levelTypeBySubscriptionModel.value]);

const facilitiesListToShow = computed(() => {
  type Result = { title?: string, elements: (ISupplier | ISupplierPreview)[] };
  const result: Result[] = [];

  if (state.searchResult.list.length) {
    result.push({
      title: "", // The modal title already describes the first list item
      elements: state.searchResult.list
    });
  }

  if (state.searchResult.listFromOtherCities.length) {
    result.push({
      title: t("In other cities"),
      elements: state.searchResult.listFromOtherCities
    });
  }

  return result;
});

const facilitiesListDefaultTitle = computed(() =>
  city.value.value ? t("Facilities in {city}", { city: city.value.value }) : t("facilities_section.searchModal.title"));

const filteredMapMarkers = computed(() => ({
  ...state.mapMarkers,
  features: state.mapMarkers.features?.filter((marker) => marker.properties.id.toString() !== TEST_ACCOUNT_ID) ?? [],
}));

const isOpenFacilityObjectModal = computed(() => {
  isFacilityObjectModalBodyScrollThresholdPass.value = false;
  return screenWidth.value < DESKTOP_SCREEN_WIDTH
    ? state.facilityId
    : facilityPopupData.value?.id || state.facilityId;
});

const payloadActivities = computed(() => {
  return activities.value.length > 0
    ? activities.value.map((e) => e.id)
    : null;
});

const payloadTags = computed(() => {
  return tags.value.length > 0 ? tags.value.map((e) => e.id) : null;
});

const reqMapMarkersBody = computed(() => {
  return {
    search: "", // Unused
    country: countriesStore.currentCountry.code,
    level: level.value?.id,
    activities: payloadActivities.value,
    tags: payloadTags.value,
    bounds: bounds.value,
    subscriptionModel: subscriptionModel.value,
  };
});

const isDisabledTeleport = computed(() => screenWidth.value >= DESKTOP_SCREEN_WIDTH);

const getQueryParam = (param: string): string | null => {
  const value = route.query[param];
  return Array.isArray(value) ? value[0] : value;
};

const getCountryBounds = (coords: CountryBoundsDependingOnOrientationScreen): [IMapLngLat, IMapLngLat] => {
  return window.matchMedia("(orientation: landscape)").matches ? coords.landscape : coords.portrait;
};

const getActiveSearchQueryKey = () => {
  const keys = [];

  keys.push(city.value?.id);
  keys.push(level.value?.id);
  keys.push(...tags.value.map((tag) => tag.id));
  keys.push(subscriptionModel.value);
  keys.push(...activities.value.map((activity) => activity.id));

  return keys.join("-");
};

async function loadSearchList() {
  searchController.controller?.abort();
  searchController.controller = new AbortController();

  if (!searchController.activeRequestCount) {
    isLoadingSearchRequest.value = true;
  }

  searchController.activeRequestCount++;

  const searchResult = {
    list: null as null | ISupplier[],
    listFromOtherCities: null as null | ISupplier[]
  };

  try {
    // If the search value exists, search in other cities as well
    // Otherwise, search for all facilities only in the current city
    const isIncludeOtherCities = !!searchString.value;

    const response = await getMapSearchList(
      {
        search: searchString.value,
        country: countriesStore.currentCountry.code,
        level: level.value?.id,
        city: city.value?.id,
        activities: payloadActivities.value,
        tags: payloadTags.value,
        includeOtherCities: isIncludeOtherCities,
        subscriptionModel: subscriptionModel.value,
      },
      searchController.controller
    );
    searchResult.list = response.searchList;
    searchResult.listFromOtherCities = response.searchListFromOtherCities ?? [];
  } catch (error) {
    console.error("Failed to load search list:", error);
  } finally {
    searchController.activeRequestCount--;
    if (!searchController.activeRequestCount) {
      isLoadingSearchRequest.value = false;
      searchController.controller = null;
    }
  }

  return searchResult;
}

const setFromHistoryOrLoadSearchList = async () => {
  if (searchString.value) {
    const searchResult = await loadSearchList();
    state.searchResult.list = searchResult.list ?? [];
    state.searchResult.listFromOtherCities = searchResult.listFromOtherCities ?? [];

    return;
  }

  const key = getActiveSearchQueryKey();
  const data = searchQueryHistory.get(key);

  if (data) {
    // We use history, so drop the search requests if they exist
    // and set data from the cache for the main data; for the rest of the data, leave it empty
    searchController.controller?.abort();
    state.searchResult.list = data;
    state.searchResult.listFromOtherCities = [];
  } else {
    const searchResult = await loadSearchList();
    if (searchResult.list) {
      state.searchResult.list = searchResult.list;
      searchQueryHistory.set(key, state.searchResult.list);

      // Make it empty because it depends on the searchValue
      state.searchResult.listFromOtherCities = [];
    }
  }
};

async function updateMapFilters() {
  try {
    const response = await getMapFilters({
      country: countriesStore.currentCountry.code,
      subscriptionModel: subscriptionModel.value,
      city: city.value?.id,
      level: level.value?.id,
      activities: payloadActivities.value,
    });

    state.filtersList = response;
    state.filtersList.cities.unshift(
      { id: "", value: t("All {country}", { country: countriesStore.currentCountry.value })}
    );
    setMapFilterLevels(response.levels);
    return structuredClone(response);
  } catch (error) {
    console.error("Failed to update map filters:", error);
    throw error;
  }
}

const debounceSearchFromHistoryOrLoad = () => {
  if (debounceTimeout) {
    clearTimeout(debounceTimeout);
  }
  debounceTimeout = setTimeout(async () => {
    await setFromHistoryOrLoadSearchList();
  }, 500) as unknown as number;
};

const handleClickCityButton = (newCity: BaseSelectOption): void => {
  if (newCity.id !== city.value?.id) {
    state.isCityButtonClicked = true;

    if (!newCity.id) {
      resetMapFilterCity();
    } else {
      setMapFilterCity({
        id: newCity.id,
        value: newCity.value,
      });
    }
  }
};

const changeLevel = async (level: ILevelFilter) => {
  setMapFilterLevel({
    ...selectedLevel.value,
    [levelTypeBySubscriptionModel.value]: level,
  });
  await Promise.all([updateMapFilters(), setFromHistoryOrLoadSearchList()]);
  await router.push(route.path);
};

const openFacilityObjectModal = (facility: ISupplierPreview) => {
  state.facilityId = facility.id;
  facilityPopupData.value = {
    id: facility.id,
    name: facility.name,
    coordinates: facility.coordinates as LngLatLike,
    address: facility.address,
  };
  state.isSelectedFacilityObject = true;
};

const closeFacilityPopup = () => {
  facilityPopupData.value = null;
};

const closeFacilityObjectModal = () => {
  facilityPopupData.value = null;
  state.facilityId = null;
  router.push({ query: undefined });
};

const openSearchModal = () => {
  state.isShowSearchModal = true;
};

const openFilterModal = () => {
  state.isShowFilterModal = true;
};

const getCurrentMapBounds = (currentBounds: [IMapLngLat, IMapLngLat]) => {
  bounds.value = currentBounds;
};

const changeScreenWidth = () => {
  screenWidth.value = window.innerWidth;
};

const initialize = async () => {
  isLoadingPage.value = true;
  changeScreenWidth();
  injectLocalStorageValues();

  const levelFromQuery = getQueryParam("level");
  const subscriptionModelFromQuery = getQueryParam("subscription-model");

  if (levelFromQuery && subscriptionModelFromQuery) {
    setSubscriptionModel(subscriptionModelFromQuery);
    setMapFilterLevel({
      ...selectedLevel.value,
      [levelTypeBySubscriptionModel.value]: {
        id: levelFromQuery,
        value: t(`levels.${levelTypeBySubscriptionModel.value}.${levelFromQuery}`),
        isAvailable: true,
        reason: "",
      },
    });
  }

  try {
    const response = await updateMapFilters();
    if (response) {
      if (!levelFromQuery && !isLevelSelected.value) {
        setMapFilterLevel(response.defaultLevel);
      }
      bounds.value = response.country.id === countriesStore.currentCountry.code
        ? getCountryBounds(response.country.bounds)
        : response.bounds;
    }
  } catch (error) {
    const fallbackResponse = await getMapFilters({
      country: countriesStore.currentCountry.code,
      subscriptionModel: subscriptionModel.value,
    });

    if (!levelFromQuery && !isLevelSelected.value) {
      setMapFilterLevel(fallbackResponse.defaultLevel);
    }

    bounds.value = fallbackResponse.country.id === countriesStore.currentCountry.code
      ? getCountryBounds(fallbackResponse.country.bounds)
      : fallbackResponse.bounds;

  } finally {
    isLoadingPage.value = false;
  }

  await setFromHistoryOrLoadSearchList();
};

watch(
  () => reqMapMarkersBody.value,
  async () => {
    if (reqMapMarkersBody.value.bounds.length) {
      isLoadingMapMarkersRequest.value = true;
      try {
        state.mapMarkers = await getMapMarkers({
          ...reqMapMarkersBody.value,
        } as IMapMarkersRequest);
      } catch (error) {
        console.error("Failed to load Map Markers:", error);
      } finally {
        isLoadingMapMarkersRequest.value = false;
      }
    }
  }
);

watch(city, async (newValue, oldValue) => {
  if (!newValue || !oldValue || newValue.value === oldValue.value) {
    return;
  }

  gtag("event", FacilitiesEvents.cityChange);
  await updateMapFilters();
  await setFromHistoryOrLoadSearchList();

  if (state.isCityButtonClicked) {
    state.flyToCity = state.filtersList.bounds;
  }
});

watch(() => searchString.value, debounceSearchFromHistoryOrLoad);
watch(() => tags.value.map((tag) => tag.id), setFromHistoryOrLoadSearchList);
watch(
  () => activities.value.map((activity) => activity.id),
  async () => {
    await updateMapFilters();
    await setFromHistoryOrLoadSearchList();
  }
);

onMounted(async () => {
  await initialize();
  window.addEventListener("resize", changeScreenWidth);
  await setFromHistoryOrLoadSearchList();
});

onUnmounted(() => {
  window.removeEventListener("resize", changeScreenWidth);
  resetSearchString();
  searchController.controller?.abort();
});

</script>
